import React, { useEffect, useRef, useState } from "react";
import { apptApi, resultApi, labelBatchApi } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Badge, StatusBadge } from "../../Components/UI/ui";
import { parseScan, RESULT_STATUS_LABEL, fmtDateTime } from "../../lib/lab";
import AppointmentDetail from "../../Screens/Appointments/AppointmentDetail";
import ResultSheet from "../../Screens/Results/ResultSheet";

// Global barcode box (specimens guide §2.5): a scanner types the code and
// presses Enter. Sticker code → its sheet(s) / booking; accession number (or
// a day prefix) → bookings. A sticker nothing knows is explained via the roll ledger.
const ScanBox = () => {
  const { toast } = useApp();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState(null);   // { sampleId, label, sheets, bookings, stray }
  const [sheet, setSheet] = useState(null);
  const [booking, setBooking] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const run = async () => {
    const { sampleId, label } = parseScan(value);
    if (!sampleId && !label) return;
    setBusy(true); setOpen(true);
    try {
      const params = sampleId ? { sampleId } : { label };
      const [rs, as] = await Promise.all([
        resultApi.list({ ...params, limit: 50 }).catch(() => ({ results: [] })),
        apptApi.list({ ...params, limit: 50 }).catch(() => ({ appointments: [] })),
      ]);
      const sheets = rs.results || [];
      const bookings = as.appointments || [];
      let stray = null;
      if (label && sheets.length === 0 && bookings.length === 0) {
        try { stray = await labelBatchApi.lookup(label); } catch (e) { stray = { error: e?.response?.data?.error || "Not recognised" }; }
      }
      setHits({ sampleId, label, sheets, bookings, stray });
      // A sticker maps to one sheet → open it straight away; an accession with one booking → open that
      if (label && sheets.length === 1) { setSheet(sheets[0]._id); setOpen(false); }
      else if (sampleId && sheets.length === 0 && bookings.length === 1) { setBooking(bookings[0]._id); setOpen(false); }
    } catch (e) { toast.error(e); } finally { setBusy(false); }
  };

  return (
    <div className="lab-scan" ref={box}>
      <input
        placeholder="Scan sticker or sample ID…"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => hits && setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter") run(); if (e.key === "Escape") setOpen(false); }}
        spellCheck={false}
        autoComplete="off"
      />
      {open && (
        <div className="lab-scan__pop">
          {busy ? <span className="lab-muted">Searching…</span> : hits && (
            <>
              <div className="lab-row between"><span className="lab-sample-id" style={{ fontSize: 15 }}>{hits.sampleId || hits.label}</span><span className="lab-muted">{hits.sheets.length} sheet(s) · {hits.bookings.length} booking(s)</span></div>
              {hits.sheets.length > 0 && (
                <>
                  <h5>Result sheets</h5>
                  {hits.sheets.map((s) => (
                    <div key={s._id} className="lab-list__item" onClick={() => { setSheet(s._id); setOpen(false); }}>
                      <div><b>{s.patientName}</b><div className="lab-muted">{s.specimenLabel || s.sampleId || ""}{s.templateSnapshot?.shortName ? ` · ${s.templateSnapshot.shortName}` : ""}</div></div>
                      <span className="lab-row">{s.sampleRejected && <Badge kind="rejected">Specimen rejected</Badge>}<Badge kind={s.status}>{RESULT_STATUS_LABEL[s.status]}</Badge></span>
                    </div>
                  ))}
                </>
              )}
              {hits.bookings.length > 0 && (
                <>
                  <h5>Bookings</h5>
                  {hits.bookings.map((a) => (
                    <div key={a._id} className="lab-list__item" onClick={() => { setBooking(a._id); setOpen(false); }}>
                      <div><b>{a.patientName}</b><div className="lab-muted">{a.sampleId || "no sample"} · {fmtDateTime(a.scheduledAt)}</div></div>
                      <StatusBadge status={a.status} />
                    </div>
                  ))}
                </>
              )}
              {hits.sheets.length === 0 && hits.bookings.length === 0 && (
                <div className="lab-muted">
                  {hits.stray?.used ? `Sticker used on booking ${hits.stray.used.labAppointmentId} (tube ${hits.stray.used.specimenCode})${hits.stray.used.manualEntry ? " · typed manually" : ""}`
                    : hits.stray?.inRange ? `Unused sticker from roll "${hits.stray.batch?.name || ""}" — not on any tube yet.`
                    : hits.stray?.error || "Not one of this lab's stickers — check the roll is registered under Settings → Label stock."}
                </div>
              )}
            </>
          )}
        </div>
      )}
      <ResultSheet id={sheet} open={!!sheet} onClose={() => setSheet(null)} />
      <AppointmentDetail id={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
};

export default ScanBox;
